import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 grain">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2.5 mb-8"><Logo className="w-7 h-7" /><Wordmark className="font-serif text-xl" /></Link>

        {!token ? (
          <div data-testid="reset-no-token">
            <h1 className="font-serif text-3xl mb-2">Invalid link</h1>
            <p className="text-sm text-muted-foreground mb-8">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">Request a new reset link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <CheckCircle2 className="w-10 h-10 text-primary mb-4" />
            <h1 className="font-serif text-3xl mb-2">Password updated</h1>
            <p className="text-sm text-muted-foreground mb-8">You can now sign in with your new password. Redirecting…</p>
            <Link to="/login" className="text-sm text-primary hover:underline">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h1 className="font-serif text-4xl mb-2">Set a new password</h1>
            <p className="text-sm text-muted-foreground mb-8">Choose a new password for your account.</p>

            {error && <div className="mb-4 text-sm text-destructive-foreground bg-destructive/30 border border-destructive px-3 py-2" data-testid="reset-error">{error}</div>}

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">New password</label>
            <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Confirm password</label>
            <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-6 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <button data-testid="reset-submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update password
            </button>

            <p className="mt-6 text-sm text-muted-foreground"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
